import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";
import type { OfflineOperation } from "../sync/types";

export type ConsumableItemType = "feed" | "medicine" | "supply" | "other";
export type InventoryMovementType = "purchase" | "usage" | "adjustment" | "waste";
export type AssetStatus = "active" | "maintenance" | "inactive" | "retired";

export interface WarehouseAssetOperationContext {
  actorId: string;
  farmId: string;
  deviceId: string;
  performedAt: string;
}

export interface WarehouseAssetOperationOptions {
  clock?: OperationClock;
  operationIdSource?: OperationIdSource;
}

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{1,39}$/;

function options(value?: WarehouseAssetOperationOptions) {
  return {
    ...(value?.clock ? { clock: value.clock } : {}),
    ...(value?.operationIdSource ? { idSource: value.operationIdSource } : {}),
  };
}

function validContext(context: WarehouseAssetOperationContext): boolean {
  return Boolean(context.actorId && context.farmId && context.deviceId && context.performedAt);
}

function finite(value: number): boolean {
  return Number.isFinite(value);
}

export function buildInventoryItemUpsertOperation(
  context: WarehouseAssetOperationContext,
  input: {
    itemId: string;
    expectedVersion: number;
    itemCode: string;
    itemType: ConsumableItemType;
    displayName: string;
    feedProductId?: string;
    baseUnit: string;
    reorderLevel: number;
    isActive: boolean;
  },
  operationOptions?: WarehouseAssetOperationOptions,
): OfflineOperation {
  const itemCode = input.itemCode.trim().toUpperCase();
  const displayName = input.displayName.trim();
  const baseUnit = input.baseUnit.trim();
  const feedProductId = input.feedProductId?.trim();

  if (
    !validContext(context) ||
    !input.itemId ||
    !Number.isInteger(input.expectedVersion) ||
    input.expectedVersion < 0 ||
    !CODE_PATTERN.test(itemCode) ||
    !["feed", "medicine", "supply", "other"].includes(input.itemType) ||
    !displayName ||
    displayName.length > 160 ||
    !baseUnit ||
    baseUnit.length > 40 ||
    !finite(input.reorderLevel) ||
    input.reorderLevel < 0
  ) {
    throw new Error("INVENTORY_ITEM_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "inventory.item.upsert",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.itemId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        itemId: input.itemId,
        expectedVersion: input.expectedVersion,
        itemCode,
        itemType: input.itemType,
        displayName,
        ...(feedProductId ? { feedProductId } : {}),
        baseUnit,
        reorderLevel: input.reorderLevel,
        isActive: input.isActive,
      },
    },
    options(operationOptions),
  );
}

export function buildInventoryMovementRecordOperation(
  context: WarehouseAssetOperationContext,
  input: {
    movementId: string;
    itemId: string;
    movementType: InventoryMovementType;
    quantityDelta: number;
    unitCostToman?: number;
    financeTransactionId?: string;
    feedingEventId?: string;
    occurredOn: string;
    notes?: string;
  },
  operationOptions?: WarehouseAssetOperationOptions,
): OfflineOperation {
  const notes = input.notes?.trim();
  const financeTransactionId = input.financeTransactionId?.trim();
  const feedingEventId = input.feedingEventId?.trim();

  const signInvalid =
    (input.movementType === "purchase" && input.quantityDelta <= 0) ||
    ((input.movementType === "usage" || input.movementType === "waste") &&
      input.quantityDelta >= 0);

  if (
    !validContext(context) ||
    !input.movementId ||
    !input.itemId ||
    !["purchase", "usage", "adjustment", "waste"].includes(input.movementType) ||
    !finite(input.quantityDelta) ||
    input.quantityDelta === 0 ||
    signInvalid ||
    (input.unitCostToman !== undefined &&
      (!finite(input.unitCostToman) || input.unitCostToman < 0)) ||
    !DATE_PATTERN.test(input.occurredOn) ||
    (notes && notes.length > 1000)
  ) {
    throw new Error("INVENTORY_MOVEMENT_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "inventory.movement.record",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.movementId,
      performedAt: context.performedAt,
      payload: {
        movementId: input.movementId,
        itemId: input.itemId,
        movementType: input.movementType,
        quantityDelta: input.quantityDelta,
        ...(input.unitCostToman === undefined ? {} : { unitCostToman: input.unitCostToman }),
        ...(financeTransactionId ? { financeTransactionId } : {}),
        ...(feedingEventId ? { feedingEventId } : {}),
        occurredOn: input.occurredOn,
        ...(notes ? { notes } : {}),
      },
    },
    options(operationOptions),
  );
}

export function buildAssetUpsertOperation(
  context: WarehouseAssetOperationContext,
  input: {
    assetId: string;
    expectedVersion: number;
    assetCode: string;
    assetName: string;
    category: string;
    quantity: number;
    locationText?: string;
    status: AssetStatus;
    purchaseValueToman?: number;
    note?: string;
    financeTransactionId?: string;
    occurredOn: string;
  },
  operationOptions?: WarehouseAssetOperationOptions,
): OfflineOperation {
  const assetCode = input.assetCode.trim().toUpperCase();
  const assetName = input.assetName.trim();
  const category = input.category.trim().toLowerCase();
  const locationText = input.locationText?.trim();
  const note = input.note?.trim();
  const financeTransactionId = input.financeTransactionId?.trim();

  if (
    !validContext(context) ||
    !input.assetId ||
    !Number.isInteger(input.expectedVersion) ||
    input.expectedVersion < 0 ||
    !CODE_PATTERN.test(assetCode) ||
    !assetName ||
    assetName.length > 180 ||
    !category ||
    category.length > 80 ||
    !finite(input.quantity) ||
    input.quantity <= 0 ||
    (locationText && locationText.length > 240) ||
    !["active", "maintenance", "inactive", "retired"].includes(input.status) ||
    (input.purchaseValueToman !== undefined &&
      (!finite(input.purchaseValueToman) || input.purchaseValueToman < 0)) ||
    (note && note.length > 1000) ||
    !DATE_PATTERN.test(input.occurredOn)
  ) {
    throw new Error("ASSET_INPUT_INVALID");
  }

  return createOfflineOperation(
    {
      operationType: "asset.upsert",
      schemaVersion: 1,
      actorId: context.actorId,
      farmId: context.farmId,
      deviceId: context.deviceId,
      entityId: input.assetId,
      performedAt: context.performedAt,
      baseVersion: String(input.expectedVersion),
      payload: {
        assetId: input.assetId,
        expectedVersion: input.expectedVersion,
        assetCode,
        assetName,
        category,
        quantity: input.quantity,
        ...(locationText ? { locationText } : {}),
        status: input.status,
        ...(input.purchaseValueToman === undefined
          ? {}
          : { purchaseValueToman: input.purchaseValueToman }),
        ...(note ? { note } : {}),
        ...(financeTransactionId ? { financeTransactionId } : {}),
        occurredOn: input.occurredOn,
      },
    },
    options(operationOptions),
  );
}
